import React from 'react';
import { Link } from 'react-router-dom';
import CreateInvoiceCollectionButton from './components/CreateInvoiceCollectionButton';
import { useAllInvoiceCollectionsQuery } from '../../graphql/queries/allInvoiceCollections.generated';
import { download, downloadJsonDataAsExcelFile } from '@/utils/download';
import { toCsvBlob } from '@/utils/data';
import { Badge, Button, Table } from 'reactstrap';
import { FaFileDownload, FaFileExcel } from 'react-icons/fa';
import i18n from '@/utils/i18n';
import { LayoutStoreInstance } from '../../LayoutStore';
import { ContentSection } from '../../components/Layout/ContentSection';
import { InvoiceCollectionScope } from '../../graphql/generatedTypes';

export function InvoiceCollectionList() {
  const { data, loading, error } = useAllInvoiceCollectionsQuery();

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  if (data?.invoiceCollections == null) {
    return <p>No invoice collections found</p>;
  }

  function toJson() {
    if (data?.invoiceCollections == null) {
      return [];
    }

    return data.invoiceCollections.map((collection) => {
      if (collection == null) {
        return null;
      }
      return Object.entries(collection).reduce((acc: any, [key, value]) => {
        if (Array.isArray(value)) {
          acc[key] = value;
        } else if (typeof value === 'object' && value !== null) {
          const flattenedObject = Object.entries(value).reduce(
            (acc2: any, [key2, value2]) => {
              if (key2 !== '__typename') {
                acc2[`${key}_${key2}`] = value2;
              }
              return acc2;
            },
            {},
          );
          return { ...acc, ...flattenedObject };
        } else {
          if (key !== '__typename') {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
    });
  }

  function exportCSV() {
    const blob = toCsvBlob(toJson());
    download(blob, `invoice_collections${new Date().toISOString()}.csv`);
  }

  async function exportExcel() {
    const jsonData = toJson();

    downloadJsonDataAsExcelFile(
      jsonData,
      `invoice_collections${new Date().toISOString()}`,
    );
  }

  return (
    <ContentSection
      title={i18n.t('Common:invoiceCollections')}
      collapsable={false}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <CreateInvoiceCollectionButton />
        <div style={{ minHeight: '50px' }}>
          <Button
            type="button"
            onClick={exportCSV}
            className="me-2"
            data-pr-tooltip="CSV"
            disabled={loading}
          >
            <FaFileDownload />
          </Button>
          <Button
            type="button"
            onClick={exportExcel}
            className="p-button-success me-2"
            data-pr-tooltip="XLS"
            disabled={loading}
          >
            <FaFileExcel />
          </Button>
        </div>
      </div>
      <br style={{ clear: 'both' }} />
      <br />
      <Table striped bordered>
        <thead>
          <tr>
            <th>{i18n.t('Common:store')}</th>
            <th>{i18n.t('Common:id')}</th>
          </tr>
        </thead>
        <tbody>
          {data.invoiceCollections
            .map((collection) => {
              if (collection == null) {
                return null;
              }
              return (
                <tr key={collection.id}>
                  <td>
                    <Link to={`/invoice-collection/${collection.id}`}>
                      {collection.name}
                    </Link>
                    {collection.scope ===
                      InvoiceCollectionScope.PenaltyBillsOnly && (
                      <Badge color="warning" pill className="ms-2">
                        {i18n.t('InvoiceCollection:scopePenaltyBillsOnly')}
                      </Badge>
                    )}
                  </td>
                  <td
                    onClick={() => {
                      navigator.clipboard.writeText(collection.id);
                      LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                    }}
                  >
                    <div>{collection.id}</div>
                  </td>
                </tr>
              );
            })
            .filter((x) => x != null)}
        </tbody>
      </Table>
    </ContentSection>
  );
}
